import type {
  PumpData,
  PumpRecommendedViscosityCorrections
} from "@/libs/calculations/pumps/pumpData";
import { computed } from "vue";
import {
  getMultiphaseTotalDynamicHeadFt,
  getPumpHeadGasDerate
} from "@/libs/calculations/pumps";
import {
  processPumpCalculations,
  getRequiredStagesForPump,
  getPumpRecCorrections,
  getPumpTorqueFtLbs
} from "@/libs/calculations/pumps/pumpData";
import { getSealBearingData } from "@/libs/calculations/seals";
import { Ratio } from "@/libs/units/percentage";
import {
  DEFAULT_ESP_PUMP_RPM,
  DEFAULT_HPS_PUMP_RPM
} from "@/libs/calculations/constants";
import type { PumpHydraulicsCtx } from "./context";

// Pump data, required/produced head and pressure, brake horsepower.
export const definePumpHydraulics = (ctx: PumpHydraulicsCtx) => {
  const {
    _equipment,
    _wellProperties,
    _parameters,
    _correlations,
    isScenarioEspShore,
    orderedIntakes,
    ascendingOrderPumps,
    primaryPump,
    bottomPump,
    primarySeal,
    bottomSeal,
    customFluidProperties,
    customViscosityProperties,
    basicData,
    fluidViscosityCPOverride,
    condensateApi,
    selectedBubblePointPressurePSIA,
    selectedDewPointPressurePSIA,
    stageFluidProperties,
    fluidPropertiesAtIntake,
    fluidPropertiesAtPumpIntake,
    fluidPropertiesAfterAllGasSeparationAtPumpContext,
    fluidPropertiesAfterPumpDischargeTubingContext,
    fluidPropertiesAfterPumpDischargeTubing,
    pumpIntakePressurePSIA,
    pumpIntakeTemperatureF,
    downholeMotorTemperatureF
  } = ctx;

  const calculatedGasHeadRerateRatio = computed(() => {
    return 1 - fluidPropertiesAtPumpIntake.value.gasFlowVolumeRatio;
  });

  const legacyGasHeadRerateRatio = computed(() =>
    getPumpHeadGasDerate(fluidPropertiesAtIntake.value.gasFlowVolumeRatio)
  );

  const overrideGasHeadRerateRatio = computed(() => {
    if (_parameters.value?.use_legacy_gas_head_rerate) {
      return legacyGasHeadRerateRatio.value;
    }
    return Ratio.toThisUnit(
      _parameters.value?.pump_override_gas_correction_percent
    );
  });

  const gasHeadRerateRatio = computed(() => {
    return (
      overrideGasHeadRerateRatio.value || calculatedGasHeadRerateRatio.value
    );
  });

  // This is just an RPM estimate to kick off the pump calcs
  // since inside of those we need initial seal bearing data.
  // This avoids a circular dependency between seal bearing data
  // and pumpData.
  const operatingRPMEstimateForPumpCalculations = computed(() => {
    const baseRPM = isScenarioEspShore.value
      ? DEFAULT_ESP_PUMP_RPM
      : DEFAULT_HPS_PUMP_RPM;
    return baseRPM * ((_parameters.value?.pump_hz || 0) / 60);
  });

  const primarySealBearingDataForPumpCalculations = computed(() => {
    const motorTemperatureF = isScenarioEspShore.value
      ? downholeMotorTemperatureF.value
      : ctx.motorSkinTemperatureF.value;
    const bearingData = getSealBearingData(
      primarySeal.value?.r_seal,
      operatingRPMEstimateForPumpCalculations.value,
      motorTemperatureF,
      _equipment.value?.r_seal_motor_oil
    );

    return {
      ...bearingData,
      sealODIn: primarySeal.value?.r_seal?.outer_diameter_inches || 0,
      sealThrustCollarODIn:
        primarySeal.value?.r_seal?.thrust_collar_outer_diameter_inches || 0,
      sealThrustCollarIDIn:
        primarySeal.value?.r_seal?.thrust_collar_inner_diameter_inches || 0,
      sealWeightLbs: primarySeal.value?.r_seal?.weight_pounds || 0
    };
  });

  const bottomSealBearingDataForPumpCalculations = computed(() => {
    const motorTemperatureF = isScenarioEspShore.value
      ? downholeMotorTemperatureF.value
      : ctx.motorSkinTemperatureF.value;
    const bearingData = getSealBearingData(
      bottomSeal.value?.r_seal,
      operatingRPMEstimateForPumpCalculations.value,
      motorTemperatureF,
      _equipment.value?.r_seal_motor_oil
    );

    return {
      ...bearingData,
      sealODIn: bottomSeal.value?.r_seal?.outer_diameter_inches || 0,
      sealThrustCollarODIn:
        bottomSeal.value?.r_seal?.thrust_collar_outer_diameter_inches || 0,
      sealThrustCollarIDIn:
        bottomSeal.value?.r_seal?.thrust_collar_inner_diameter_inches || 0,
      sealWeightLbs: bottomSeal.value?.r_seal?.weight_pounds || 0
    };
  });

  const getPumpDataForRpmCorrection = (rpmCorrection: number): PumpData => {
    return processPumpCalculations(
      _equipment.value?.r_motor,
      ascendingOrderPumps.value,
      orderedIntakes.value,
      pumpIntakePressurePSIA.value,
      pumpIntakeTemperatureF.value,
      _parameters.value?.pump_hz,
      _parameters.value?.has_compression_pump as boolean,
      gasHeadRerateRatio.value,
      basicData.value,
      fluidPropertiesAfterAllGasSeparationAtPumpContext.value,
      fluidPropertiesAtIntake.value,
      _correlations.value,
      _parameters.value?.pump_wear_brake_horsepower_correction_ratio,
      _parameters.value?.pump_wear_head_correction_ratio,
      isScenarioEspShore.value,
      customFluidProperties.value,
      customViscosityProperties.value,
      selectedBubblePointPressurePSIA.value,
      selectedDewPointPressurePSIA.value,
      condensateApi.value,
      fluidViscosityCPOverride.value,
      primarySealBearingDataForPumpCalculations.value,
      bottomSealBearingDataForPumpCalculations.value,
      rpmCorrection
    );
  };

  const basePumpData = computed<PumpData>(() => {
    return getPumpDataForRpmCorrection(1);
  });

  const pumpData = computed<PumpData>(() => {
    const rpmCorrection = ctx.rpmCorrectionForPumpCalculations.value;
    // Without an RPM correction this is the same calculation as basePumpData,
    // so share its result rather than computing every pump stage twice (it
    // is the most expensive part of an evaluation). Nothing mutates either.
    if (rpmCorrection === 1) return basePumpData.value;
    return getPumpDataForRpmCorrection(rpmCorrection);
  });

  const averageLiquidDensityLBMFT3 = computed(() => {
    // If there is at least one pump we use the real densities calculated for them
    if (ascendingOrderPumps.value.length) {
      const densitySet = ascendingOrderPumps.value.flatMap(xrefPump => {
        return pumpData.value.individualPumpCalculations[
          xrefPump.id
        ].stageCalculations.map(stageCalcs => [
          stageCalcs.intakeFluidPropertiesContext.fluidProperties
            .liquidDensityLBMFT3,
          stageCalcs.generatedHeadFt
        ]);
      });

      const totalValue = densitySet.reduce((sum, [val, weight]) => {
        return (sum += val * weight);
      }, 0);

      const totalWeight = densitySet.reduce(
        (sum, [_, weight]) => sum + weight, // eslint-disable-line @typescript-eslint/no-unused-vars
        0
      );

      return totalWeight ? totalValue / totalWeight : 0;
    }
    // If there are no pumps we use our required (estimated) density
    return fluidPropertiesAtPumpIntake.value.liquidDensityLBMFT3;
  });

  const dischargeFluidDensityLBMFT3 = computed(() => {
    // If there is at least one pump we use the real producing fluid density
    // from the pump calcs
    if (primaryPump.value) {
      return (
        pumpData.value?.individualPumpCalculations?.[primaryPump.value?.id]
          ?.dischargeStageCalculations?.dischargeFluidPropertiesContext
          .fluidProperties?.fluidDensityLBMFT3 || 0
      );
    }
    // If there is no pump selected we use our required (estimated) fluid density
    return fluidPropertiesAfterPumpDischargeTubingContext.value?.fluidProperties
      .fluidDensityLBMFT3;
  });

  // This is the average fluid density from the pump calculations
  const averageFluidDensityLBMFT3 = computed(
    () =>
      // Average fluid density prior to pumps and last pump discharge
      (fluidPropertiesAtPumpIntake.value.fluidDensityLBMFT3 +
        dischargeFluidDensityLBMFT3.value) /
      2
  );

  const requiredPumpBrakeHP = computed(() => {
    return pumpData.value.requiredPumpBrakeHP;
  });

  const pumpTorqueFtLbs = computed(() => {
    return getPumpTorqueFtLbs(
      ctx.operatingRPM.value,
      requiredPumpBrakeHP.value
    );
  });

  const requiredDischargePressurePSIA = computed(() => {
    if (isScenarioEspShore.value) {
      // THIS HAS PUMP INTAKE PRESSURE BAKED INTO IT (wellFlowingPressureAtPumpPSIA)
      // inside the pipeline function which we assign to PIP here
      return stageFluidProperties.value.requiredDischargePressurePSIA;
    }
    // If HPS this is given by the user
    return _wellProperties.value?.hps_required_discharge_pressure_psia;
  });

  // Subtract PIP from required discharge pressure
  const differentialPressurePSIA = computed(() => {
    return requiredDischargePressurePSIA.value - pumpIntakePressurePSIA.value;
  });

  // Calculate required TDH from required differential pressure and produced
  // fluid density from pumps. We use the one from pumps (when) there are pumps
  // because we need a baseline to share with produced TDH so we make sure they
  // both use the same density
  const requiredTotalDynamicHeadFt = computed(() => {
    return getMultiphaseTotalDynamicHeadFt(
      differentialPressurePSIA.value,
      dischargeFluidDensityLBMFT3.value
    );
  });

  // Pipeline calcs still affect produced b/c of PIP
  const producedDischargePressurePSIA = computed(() => {
    if (primaryPump.value) {
      // THIS HAS PUMP INTAKE PRESSURE BAKED INTO IT
      // .dischargePressure includes the pressure from the stage + incoming pressure
      // pressure so the first stage has the incoming PIP included
      const pumpDischargePressure =
        pumpData.value.individualPumpCalculations[primaryPump.value?.id]
          ?.dischargeStageCalculations?.dischargePressurePSIA || 0;
      return pumpDischargePressure;
    }
    // If there is no pump the PIP is the default produced
    return pumpIntakePressurePSIA.value;
  });

  // Subtract PIP from produced discharge pressure for our
  // 'differential produced pressure'
  const producedTotalDynamicHeadFt = computed(() => {
    return getMultiphaseTotalDynamicHeadFt(
      producedDischargePressurePSIA.value - pumpIntakePressurePSIA.value,
      dischargeFluidDensityLBMFT3.value
    );
  });

  // Discharge from the last pump
  const dischargeFlowRateSTBD = computed(() => {
    const primaryPumpData =
      pumpData.value.individualPumpCalculations[primaryPump.value?.id];
    return (
      primaryPumpData?.dischargeStageCalculations
        ?.dischargeFluidPropertiesContext.flowRatesInSitu?.fluidFlowRateBBLD ||
      0
    );
  });

  // This really isn't STBD, it's BBLD since it's flow rate at the bottom pump
  // This is intake flow rate into the bottom-most pump
  const intakeFlowRateSTBD = computed(() => {
    const bottomPumpData =
      pumpData.value.individualPumpCalculations[bottomPump.value?.id];
    return (
      bottomPumpData?.intakeStageCalculations?.intakeFluidPropertiesContext
        .flowRatesInSitu?.fluidFlowRateBBLD || 0
    );
  });

  const operatingThrustLoad = computed(() => {
    const primaryPumpData =
      pumpData.value.individualPumpCalculations[primaryPump.value?.id];
    return primaryPumpData?.operatingThrustLoad || 0;
  });

  const requiredSealBrakeHP = computed(() => {
    return pumpData.value.requiredSealBrakeHP;
  });

  const requiredIntakeBrakeHP = computed(() => {
    return pumpData.value.requiredIntakeBrakeHP;
  });

  const totalRequiredBrakeHP = computed(
    () =>
      (requiredPumpBrakeHP.value || 0) +
      (requiredSealBrakeHP.value || 0) +
      (requiredIntakeBrakeHP.value || 0)
  );

  const totalRequired60HzBrakeHP = computed(
    () => totalRequiredBrakeHP.value * (60 / _parameters.value?.pump_hz)
    // This might replace this function when it is decided by the gods
    // totalRequiredBrakeHP.value * Math.pow(60 / _parameters.value?.pump_hz, 3)
  );

  const pumpRequiredStages = computed(() => {
    // Pumps can be inserted in any order, so each pump's remaining required
    // pressure should account for pressure already produced by every other pump.
    const pumpProducedLiftHeadById = ascendingOrderPumps.value.reduce<
      Record<string, number>
    >((acc, xrefPump) => {
      const pumpGeneratedLiftHeadFt =
        pumpData.value.individualPumpCalculations[xrefPump.id]
          ?.pumpGeneratedLiftHeadFt;
      acc[xrefPump.id] = Number.isFinite(pumpGeneratedLiftHeadFt)
        ? pumpGeneratedLiftHeadFt
        : 0;
      return acc;
    }, {});

    // Total pressure generated by pumps (PIP not included)
    const totalProducedLiftHeadFt = Object.values(
      pumpProducedLiftHeadById
    ).reduce((sum, pumpLiftHeadFt) => sum + pumpLiftHeadFt, 0);

    // PIP not included
    const targetRequiredLiftHeadFt = getMultiphaseTotalDynamicHeadFt(
      differentialPressurePSIA.value,
      fluidPropertiesAfterPumpDischargeTubing.value.fluidDensityLBMFT3
    );

    return ascendingOrderPumps.value.reduce<
      Record<string, { requiredStages: number }>
    >((acc, xrefPump) => {
      // Head produced by this pump (PIP not included)
      const pumpProducedLiftHeadFt = pumpProducedLiftHeadById[xrefPump.id] || 0;
      const liftProducedByOtherPumps =
        totalProducedLiftHeadFt - pumpProducedLiftHeadFt;

      // Remaining lift
      const remainingRequiredLiftHeadFt = Math.max(
        targetRequiredLiftHeadFt - liftProducedByOtherPumps,
        0
      );

      acc[xrefPump.id] = {
        requiredStages: getRequiredStagesForPump(
          xrefPump,
          remainingRequiredLiftHeadFt,
          pumpProducedLiftHeadFt
        )
      };

      return acc;
    }, {});
  });

  const pumpRecommendedViscosityCorrections = computed(() => {
    return ascendingOrderPumps.value.reduce<PumpRecommendedViscosityCorrections>(
      (pumpRecCorrs, xrefPump) => {
        pumpRecCorrs[xrefPump.id] = getPumpRecCorrections(
          xrefPump,
          _parameters.value?.target_liquid_rate_std_barrel_day,
          ctx.averageViscosityCP.value,
          ctx.operatingRPM.value,
          ctx.averageLiquidSpecificGravity.value
        );
        return pumpRecCorrs;
      },
      {}
    );
  });

  return {
    calculatedGasHeadRerateRatio,
    legacyGasHeadRerateRatio,
    overrideGasHeadRerateRatio,
    gasHeadRerateRatio,
    operatingRPMEstimateForPumpCalculations,
    primarySealBearingDataForPumpCalculations,
    bottomSealBearingDataForPumpCalculations,
    getPumpDataForRpmCorrection,
    basePumpData,
    pumpData,
    averageLiquidDensityLBMFT3,
    dischargeFluidDensityLBMFT3,
    averageFluidDensityLBMFT3,
    requiredPumpBrakeHP,
    pumpTorqueFtLbs,
    requiredDischargePressurePSIA,
    differentialPressurePSIA,
    requiredTotalDynamicHeadFt,
    producedDischargePressurePSIA,
    producedTotalDynamicHeadFt,
    dischargeFlowRateSTBD,
    intakeFlowRateSTBD,
    operatingThrustLoad,
    requiredSealBrakeHP,
    requiredIntakeBrakeHP,
    totalRequiredBrakeHP,
    totalRequired60HzBrakeHP,
    pumpRequiredStages,
    pumpRecommendedViscosityCorrections
  };
};

export type PumpHydraulicsCalcs = ReturnType<typeof definePumpHydraulics>;
